import { create } from 'zustand';
import { cloudRepository } from '../data/cloud';
import { getCareerProgress, getDatasetOwnerId, getDatasetSyncState, getLatestImport, runRepository, savedViewRepository, saveDatasetSyncState, settingsRepository } from '../data/repositories';
import { EMPTY_FILTER } from '../domain/schemas';
import { authConfigured } from '../lib/api';
import type {
  AdminAccount,
  CareerProgress,
  CloudDatasetSummary,
  CohortSpec,
  DataScope,
  FilterSpec,
  ImportResult,
  NormalizedRunV2,
  OverallMeta,
  OverallDataSummary,
  SavedView,
  Settings
} from '../domain/types';
import { parseFilterFromSearch, serializeFilterToSearch } from './filterUrl';
import { useAuthStore } from './authStore';

type AppState = {
  runs: NormalizedRunV2[];
  progress: CareerProgress | null;
  localRuns: NormalizedRunV2[];
  localProgress: CareerProgress | null;
  dataScope: DataScope;
  viewedAccount: AdminAccount | null;
  overallSummary: OverallDataSummary | null;
  overallMeta: OverallMeta | null;
  latestDataset: CloudDatasetSummary | null;
  cloudLoading: boolean;
  settings: Settings | null;
  savedViews: SavedView[];
  filter: FilterSpec;
  filterHistory: FilterSpec[];
  cohorts: CohortSpec[];
  loading: boolean;
  workerBusy: boolean;
  hydrated: boolean;
  lastRefresh: number | null;
  error: string | null;
  hydrate: () => Promise<void>;
  reloadRuns: () => Promise<void>;
  switchDataScope: (scope: Exclude<DataScope, 'admin'>) => Promise<void>;
  viewAdminAccount: (account: AdminAccount) => Promise<void>;
  uploadLatestDataset: (delta?: ImportResult['delta']) => Promise<CloudDatasetSummary>;
  refreshLatestDataset: () => Promise<void>;
  setRuns: (runs: NormalizedRunV2[]) => void;
  setFilter: (filter: FilterSpec, pushHistory?: boolean) => void;
  patchFilter: (patch: Partial<FilterSpec>) => void;
  clearFilters: () => void;
  undoFilter: () => void;
  setCohort: (cohort: CohortSpec) => void;
  setWorkerBusy: (busy: boolean) => void;
  setError: (error: string | null) => void;
  saveSettings: (settings: Settings) => Promise<void>;
  refreshSavedViews: () => Promise<void>;
};

const baseCohort = (id: 'A' | 'B', name: string): CohortSpec => ({ id, name, filter: { ...EMPTY_FILTER } });

function syncUrl(filter: FilterSpec) {
  if (typeof window === 'undefined') return;
  const next = `${window.location.pathname}${serializeFilterToSearch(filter, window.location.search)}${window.location.hash}`;
  window.history.replaceState(null, '', next);
}

export const useAppStore = create<AppState>((set, get) => ({
  runs: [],
  progress: null,
  localRuns: [],
  localProgress: null,
  dataScope: 'personal',
  viewedAccount: null,
  overallSummary: null,
  overallMeta: null,
  latestDataset: null,
  cloudLoading: false,
  settings: null,
  savedViews: [],
  filter: typeof window === 'undefined' ? { ...EMPTY_FILTER } : parseFilterFromSearch(window.location.search),
  filterHistory: [],
  cohorts: [baseCohort('A', '基准组 A'), baseCohort('B', '对照组 B')],
  loading: true,
  workerBusy: false,
  hydrated: false,
  lastRefresh: null,
  error: null,

  async hydrate() {
    if (get().hydrated) return;
    set({ loading: true });
    try {
      const [storedRuns, storedProgress, settings, savedViews, datasetOwnerId, latestImport, datasetSync] = await Promise.all([
        runRepository.list('all'),
        getCareerProgress(),
        settingsRepository.get(),
        savedViewRepository.list(),
        getDatasetOwnerId(),
        getLatestImport(),
        getDatasetSyncState()
      ]);
      const accountId = authConfigured ? useAuthStore.getState().profile?.id || null : null;
      const ownsLocalDataset = !authConfigured || Boolean(accountId && datasetOwnerId === accountId);
      const localRuns = ownsLocalDataset ? storedRuns : [];
      const localProgress = ownsLocalDataset ? storedProgress : null;

      // Make the local normalized cache available immediately. Cloud checks continue in
      // the background so a slow network never hides an already usable local dataset.
      set({
        runs: localRuns,
        progress: localProgress,
        localRuns,
        localProgress,
        settings,
        savedViews,
        hydrated: true,
        loading: false,
        cloudLoading: authConfigured
      });

      if (!authConfigured || !accountId) return;
      void (async () => {
        const hasOwnedLocalDataset = storedRuns.some((run) => run.sourceScope === 'history') || Boolean(storedProgress);
        const localImportedAt = latestImport?.at || 0;
        try {
          const cloudDataset = await cloudRepository.getMyLatestDataset();
          const localChanged = hasOwnedLocalDataset && (
            !datasetSync || datasetSync.ownerId !== accountId || datasetSync.importAt !== localImportedAt
          );

          if (hasOwnedLocalDataset && (!cloudDataset || localChanged)) {
            const uploaded = cloudDataset
              ? await cloudRepository.syncLatestData(storedRuns, storedProgress, datasetSync, latestImport?.result.delta)
              : await cloudRepository.uploadLatestDataWithState(storedRuns, storedProgress);
            await saveDatasetSyncState(accountId, uploaded.dataset, localImportedAt, uploaded.sync).catch(() => undefined);
            set({ latestDataset: uploaded.dataset });
            return;
          }

          if (hasOwnedLocalDataset && cloudDataset && datasetSync?.datasetId === cloudDataset.id && (!cloudDataset.revision || datasetSync.revision === cloudDataset.revision)) {
            set({ latestDataset: cloudDataset });
            return;
          }

          if (cloudDataset) {
            const cloud = await cloudRepository.getMyLatestData();
            if (cloud.dataset) {
              await runRepository.cacheCloudDataset(cloud.runs, cloud.progress, accountId, cloud.dataset);
              set({
                runs: cloud.runs,
                progress: cloud.progress,
                localRuns: cloud.runs,
                localProgress: cloud.progress,
                latestDataset: cloud.dataset,
                lastRefresh: Date.now()
              });
            }
          }
        } catch (cloudError) {
          set({ error: `${hasOwnedLocalDataset ? '云端更新检查失败，已继续使用本账号的本地副本' : '云端存档读取失败，且没有属于本账号的本地副本'}：${cloudError instanceof Error ? cloudError.message : String(cloudError)}` });
        } finally {
          set({ cloudLoading: false });
        }
      })();
    } catch (error) {
      set({ error: error instanceof Error ? error.message : String(error), hydrated: true, loading: false });
    }
  },

  async reloadRuns() {
    const [runs, progress] = await Promise.all([runRepository.list('all'), getCareerProgress()]);
    const active = get().dataScope === 'personal';
    set({
      localRuns: runs,
      localProgress: progress,
      ...(active ? { runs, progress } : {}),
      lastRefresh: Date.now()
    });
  },

  async switchDataScope(scope) {
    if (scope === 'personal') {
      set({
        dataScope: 'personal',
        viewedAccount: null,
        runs: get().localRuns,
        progress: get().localProgress,
        overallSummary: null,
        overallMeta: null,
        cloudLoading: false
      });
      return;
    }
    set({ cloudLoading: true, error: null });
    try {
      const meta = await cloudRepository.getOverallMeta();
      set({
        dataScope: 'overall',
        viewedAccount: null,
        runs: [],
        progress: null,
        overallSummary: meta,
        overallMeta: meta,
        cloudLoading: false,
        lastRefresh: Date.now()
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : String(error), cloudLoading: false });
      throw error;
    }
  },

  async viewAdminAccount(account) {
    set({ cloudLoading: true, error: null });
    try {
      const data = await cloudRepository.getAdminAccountData(account.id);
      set({
        dataScope: 'admin',
        viewedAccount: account,
        runs: data.runs,
        progress: data.progress,
        overallSummary: null,
        overallMeta: null,
        cloudLoading: false,
        lastRefresh: Date.now()
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : String(error), cloudLoading: false });
      throw error;
    }
  },

  async uploadLatestDataset(delta) {
    set({ cloudLoading: true, error: null });
    try {
      const [accountId, latestImport, syncState] = [useAuthStore.getState().profile?.id || null, await getLatestImport(), await getDatasetSyncState()];
      const uploaded = await cloudRepository.syncLatestData(get().localRuns, get().localProgress, syncState, delta);
      if (accountId) await saveDatasetSyncState(accountId, uploaded.dataset, latestImport?.at || 0, uploaded.sync).catch(() => undefined);
      const latestDataset = { ...uploaded.dataset, syncMode: uploaded.mode };
      set({ latestDataset, cloudLoading: false });
      return latestDataset;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : String(error), cloudLoading: false });
      throw error;
    }
  },

  async refreshLatestDataset() {
    try { set({ latestDataset: await cloudRepository.getMyLatestDataset() }); }
    catch (error) { set({ error: error instanceof Error ? error.message : String(error) }); }
  },

  setRuns: (runs) => set({ runs }),
  setFilter(filter, pushHistory = true) {
    const current = get().filter;
    syncUrl(filter);
    set({ filter, filterHistory: pushHistory ? [...get().filterHistory.slice(-9), current] : get().filterHistory });
  },
  patchFilter(patch) {
    get().setFilter({ ...get().filter, ...patch });
  },
  clearFilters() {
    get().setFilter({ ...EMPTY_FILTER });
  },
  undoFilter() {
    const history = [...get().filterHistory];
    const previous = history.pop();
    if (!previous) return;
    syncUrl(previous);
    set({ filter: previous, filterHistory: history });
  },
  setCohort(cohort) {
    set({ cohorts: get().cohorts.map((item) => item.id === cohort.id ? cohort : item) });
  },
  setWorkerBusy: (workerBusy) => set({ workerBusy }),
  setError: (error) => set({ error }),
  async saveSettings(settings) {
    await settingsRepository.save(settings);
    set({ settings });
  },
  async refreshSavedViews() {
    set({ savedViews: await savedViewRepository.list() });
  }
}));
